/**
 * Centralized logger.
 *
 * All application code routes through this module — never call console.log directly.
 * In production, swap the adapter to ship logs to a remote service (Datadog, Sentry, etc.).
 *
 * Every network request must emit a debug log. Errors must include context.
 *
 * @spec SPEC:ENGINEERING:FRONTEND:LOGGING
 *
 * Usage:
 *   import { logger } from '@/services/logger'
 *   logger.debug('user.profile fetched', { userId: '123', duration: 42 })
 *   logger.error('auth.refresh failed', error, { userId: '123' })
 */
import { isDev } from '@/env'

type LogLevel = 'debug' | 'info' | 'warn' | 'error'
type LogContext = Record<string, unknown>

interface LogEntry {
  level: LogLevel
  message: string
  context?: LogContext | undefined
  error?: unknown | undefined
  timestamp: string
}

function formatEntry(entry: LogEntry): string {
  const base = `[${entry.timestamp}] [${entry.level.toUpperCase()}] ${entry.message}`
  const ctx = entry.context ? ` ${JSON.stringify(entry.context)}` : ''
  return base + ctx
}

function log(level: LogLevel, message: string, errorOrContext?: unknown, context?: LogContext) {
  const isError = errorOrContext instanceof Error

  const entry: LogEntry = {
    level,
    message,
    timestamp: new Date().toISOString(),
    context: isError ? context : (errorOrContext as LogContext | undefined),
    error: isError ? errorOrContext : undefined,
  }

  if (isDev) {
    const formatted = formatEntry(entry)
    if (level === 'error') {
      console.error(formatted, entry.error ?? '')
    } else if (level === 'warn') {
      console.warn(formatted)
    } else if (level === 'debug') {
      console.debug(formatted)
    } else {
      console.info(formatted)
    }
    return
  }

  // Production: structured JSON to stdout — pipe to CloudWatch / log aggregator
  // Replace this with your remote logging adapter (Datadog, Sentry breadcrumb, etc.)
  const structured = {
    ...entry,
    error: entry.error instanceof Error
      ? { message: entry.error.message, stack: entry.error.stack }
      : entry.error,
  }
  console.log(JSON.stringify(structured))
}

export const logger = {
  debug: (message: string, context?: LogContext) => log('debug', message, context),
  info: (message: string, context?: LogContext) => log('info', message, context),
  warn: (message: string, context?: LogContext) => log('warn', message, context),
  error: (message: string, error?: unknown, context?: LogContext) =>
    log('error', message, error, context),
}
